import { useEffect, useMemo, useState } from "react";
import type { EasyModeProgress, EasyModeStep } from "../types";
import { easyModeProgressPercent } from "../utils/easyMode";

interface EasyModeProgressPanelProps {
  progress: EasyModeProgress;
  onDismiss: () => void;
  onOpenStoryboards: () => void;
}

function formatElapsed(startedAt: number, endedAt: number): string {
  const totalSeconds = Math.max(0, Math.floor((endedAt - startedAt) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function stepIcon(step: EasyModeStep) {
  if (step.status === "done") {
    return <span className="easy-step-icon done" aria-hidden="true">✓</span>;
  }
  if (step.status === "warning" || step.status === "failed") {
    return <span className="easy-step-icon warn" aria-hidden="true">!</span>;
  }
  if (step.status === "running") {
    return <span className="easy-step-icon spin" aria-hidden="true" />;
  }
  return <span className="easy-step-icon todo" aria-hidden="true" />;
}

function stepStatusText(step: EasyModeStep): string {
  if (step.status === "running") return "in progress";
  if (step.status === "done") return "complete";
  if (step.status === "warning") return "completed with warnings";
  if (step.status === "failed") return "failed";
  return "waiting";
}

/**
 * Floating Easy Mode progress meter: live per-stage status while the one-tap pipeline runs, then a
 * completion summary with warnings. Pure presentation — all state lives in App's EasyModeProgress.
 */
export function EasyModeProgressPanel({ progress, onDismiss, onOpenStoryboards }: EasyModeProgressPanelProps) {
  const [now, setNow] = useState(() => Date.now());
  const [showWarnings, setShowWarnings] = useState(false);
  const finished = Boolean(progress.finishedAt);
  const percent = easyModeProgressPercent(progress);
  const hasIssues = useMemo(
    () => progress.warnings.length > 0 || progress.steps.some((step) => step.status === "warning" || step.status === "failed"),
    [progress]
  );

  useEffect(() => {
    if (finished) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [finished]);

  const elapsed = formatElapsed(progress.startedAt, progress.finishedAt || now);

  return (
    <aside className={`easy-progress-panel${finished ? " finished" : ""}`} role="status" aria-live="polite" aria-label="Easy Mode progress">
      <header>
        <strong>
          ✨ {finished ? (hasIssues ? "Easy Mode finished with warnings" : "Easy Mode complete") : "Easy Mode running…"}
        </strong>
        <small>{progress.scopeLabel} · {elapsed}</small>
        <button className="easy-progress-dismiss" onClick={onDismiss} type="button" aria-label="Dismiss Easy Mode progress">
          ×
        </button>
      </header>
      <div
        className="easy-progress-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <span className={finished && !hasIssues ? "complete" : ""} style={{ width: `${percent}%` }} />
      </div>
      <ul className="easy-progress-steps">
        {progress.steps.map((step) => (
          <li className={`easy-progress-step ${step.status}`} key={step.id}>
            {stepIcon(step)}
            <span className="easy-step-label">
              {step.label}
              <em className="visually-hidden"> — {stepStatusText(step)}</em>
            </span>
            <small>{step.detail}</small>
          </li>
        ))}
      </ul>
      {finished && progress.warnings.length ? (
        <button
          className="easy-progress-warning-chip"
          onClick={() => setShowWarnings((current) => !current)}
          type="button"
          aria-expanded={showWarnings}
        >
          {progress.warnings.length} warning{progress.warnings.length === 1 ? "" : "s"} — review before render
        </button>
      ) : null}
      {finished && showWarnings && progress.warnings.length ? (
        <ul className="easy-progress-warning-list">
          {progress.warnings.map((warning, index) => (
            <li key={index}>{warning}</li>
          ))}
        </ul>
      ) : null}
      {finished ? (
        <div className="easy-progress-actions">
          <button className="secondary-action compact-action" onClick={onDismiss} type="button">
            Dismiss
          </button>
          <button className="primary-action compact-action" onClick={onOpenStoryboards} type="button">
            Open Storyboards
          </button>
        </div>
      ) : (
        <p className="easy-progress-footnote">Dry-run safe · nothing is sent to the video provider</p>
      )}
    </aside>
  );
}
